import { useEffect, useState } from 'react'
import { useNavigate, type NavigateFunction } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { OperationVerificationDocumentField } from '@/components/venue/OperationVerificationDocumentField'
import { useOperationVerificationPrefill } from '@/hooks/useOperationVerificationPrefill'
import { useSubmitOperationVerification } from '@/hooks/useSubmitOperationVerification'
import { toApiError } from '@/lib/api/errors'
import { formatDate } from '@/lib/venue/format'
import { BUSINESS_TYPE_CATALOG, type OperationVerificationView } from '@/lib/venue/operationVerificationTypes'
import {
  isOperationVerificationFormComplete,
  isReviewPending,
  OPERATION_VERIFICATION_LIMITS,
  prefillToFormState,
  todayKstDate,
  toOperationVerificationSubmitInput,
  validateOperationVerificationForm,
  type OperationVerificationFormState,
} from '@/lib/venue/operationVerificationViewModel'
import { toFieldErrorMap } from '@/lib/venue/venueProfileViewModel'
import { useVenueContext } from '@/routes/venueContext'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

const REVIEW_DURATION_NOTICE = '검토는 영업일 기준 3~5일, 결과는 이메일과 앱 알림으로 안내됩니다.'

const DEADLINE_WARNING =
  '인증 기한 안에 운영 인증을 완료하지 않으면 구장이 사용자 목록과 검색 결과에서 제외되며 신규 온라인 예약을 받을 수 없습니다.'

/**
 * 운영 인증 신청 — Figma `운영 인증 신청`(14039-39778, design.md §2).
 *
 * 진입 즉시 EP-3(prefill)로 "심사 중인 회차가 있는가"를 판정해 진행중 안내/폼 중 하나를 그린다
 * (design.md §2 자율 결정 3). 🚨 게이팅 대상이 아니다 — 인증하러 오는 입구라 어떤 상태에서도 열린다
 * (api-spec §1).
 */
export function VenueOperationVerificationPage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()
  const prefillQuery = useOperationVerificationPrefill(venueId)
  const submitMutation = useSubmitOperationVerification(venueId)

  const [form, setForm] = useState<OperationVerificationFormState | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [pending, setPending] = useState(false)
  const [alreadySubmittedNotice, setAlreadySubmittedNotice] = useState(false)
  const [submitted, setSubmitted] = useState<OperationVerificationView | null>(null)

  // 조회가 도착하면 폼/진행중 상태를 한 번 초기화한다. 이후 편집 중에는 재조회로 덮어쓰지 않는다.
  useEffect(() => {
    if (!prefillQuery.data || form !== null || submitted) return
    if (isReviewPending(prefillQuery.data)) {
      setPending(true)
      return
    }
    setForm(prefillToFormState(prefillQuery.data))
  }, [prefillQuery.data, form, submitted])

  if (prefillQuery.isLoading) {
    return (
      <>
        <PageHeader title="운영 인증 신청" />
        <LoadingState rows={4} label="신청 정보를 불러오는 중" />
      </>
    )
  }

  if (prefillQuery.isError) {
    return (
      <>
        <PageHeader title="운영 인증 신청" />
        <ErrorState
          error={prefillQuery.error}
          onRetry={() => {
            void prefillQuery.refetch()
          }}
        />
      </>
    )
  }

  if (submitted) {
    return <SubmittedNotice venueId={venueId} result={submitted} navigate={navigate} />
  }

  if (pending) {
    return <PendingReviewNotice venueId={venueId} navigate={navigate} showAlreadySubmittedNotice={alreadySubmittedNotice} />
  }

  if (!form) return null

  function updateField<K extends keyof OperationVerificationFormState>(
    key: K,
    value: OperationVerificationFormState[K]
  ) {
    setForm((current) => (current ? { ...current, [key]: value } : current))
  }

  const hasUploadingDocument = form.document.status === 'uploading'
  // 🚨 `#887` — 필수값(대표자 성명·사업자등록번호·연락처) 미입력 시 제출 버튼 자체를 비활성화한다.
  // 형식 유효성은 여기서 보지 않는다 — 그건 클릭 후 필드 에러로 안내한다(design.md §2 자율 결정 2).
  const canSubmit = isOperationVerificationFormComplete(form)

  async function handleSubmit() {
    if (!form || hasUploadingDocument) return

    const clientErrors = validateOperationVerificationForm(form)
    if (clientErrors.length > 0) {
      setFieldErrors(toFieldErrorMap(clientErrors))
      return
    }
    setFieldErrors({})

    try {
      const result = await submitMutation.mutateAsync(toOperationVerificationSubmitInput(form))
      setSubmitted(result)
    } catch (error) {
      const apiError = toApiError(error)
      if (apiError.fieldErrors) {
        setFieldErrors(toFieldErrorMap(apiError.fieldErrors))
        return
      }
      // 🚨 409(대기 중 신청 존재) — 재조회하지 않고 진행중 안내로 전환한다(api-spec §171).
      if (apiError.status === 409) {
        setAlreadySubmittedNotice(true)
        setPending(true)
      }
    }
  }

  // 필드 오류(400)·409(위에서 진행중 안내로 전환)는 이미 각자 표시되므로 배너를 중복으로 띄우지
  // 않는다 — 서류 위반·S3 오류·그 밖의 실패만 배너로 안내한다(`VenueProfileEditPage.tsx` 관례).
  const submitApiError = submitMutation.isError ? toApiError(submitMutation.error) : null
  const submitError = submitApiError && !submitApiError.fieldErrors && submitApiError.status !== 409 ? submitApiError : null

  return (
    <>
      <PageHeader title="운영 인증 신청" description="사업자 정보를 입력해 운영 인증을 신청합니다." />

      <div className="mb-5 space-y-3">
        <Alert tone="warning">{DEADLINE_WARNING}</Alert>
        {submitError ? <Alert tone="danger">{submitError.message}</Alert> : null}
      </div>

      <div className="space-y-5">
        <Card>
          <CardHeader title="사업자 정보" />
          <CardBody className="space-y-4">
            <div>
              <label
                className="mb-1.5 block text-sm font-medium text-ink-700"
                htmlFor="operation-verification-business-type"
              >
                사업자 유형
              </label>
              <Select
                id="operation-verification-business-type"
                value={form.businessType}
                onChange={(event) => {
                  updateField('businessType', event.target.value as OperationVerificationFormState['businessType'])
                }}
              >
                {BUSINESS_TYPE_CATALOG.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label
                className="mb-1.5 block text-sm font-medium text-ink-700"
                htmlFor="operation-verification-representative-name"
              >
                대표자 성명
              </label>
              <Input
                id="operation-verification-representative-name"
                value={form.representativeName}
                maxLength={OPERATION_VERIFICATION_LIMITS.representativeName}
                aria-invalid={Boolean(fieldErrors.representativeName)}
                disabled
                readOnly
                className="cursor-not-allowed bg-slateish-50 text-slateish-500"
                onChange={() => {
                  // 🚨 읽기전용 — 서버가 회원 실명으로 확정하고 요청 본문의 값을 무시한다(`#867` 자율
                  // 결정 5). 편집 가능하게 두면 반영되지 않는 입력을 사용자가 고치게 된다.
                }}
              />
              <p className="mt-1 text-xs text-slateish-500">본인인증된 실명이 자동 입력되며 수정할 수 없습니다.</p>
              {form.representativeName.trim().length === 0 ? (
                <p className="mt-1 text-xs text-danger-500">본인인증 후 신청할 수 있습니다.</p>
              ) : null}
              <FieldError message={fieldErrors.representativeName} />
            </div>

            <div>
              <label
                className="mb-1.5 block text-sm font-medium text-ink-700"
                htmlFor="operation-verification-business-number"
              >
                사업자등록번호
              </label>
              <Input
                id="operation-verification-business-number"
                value={form.businessNumber}
                aria-invalid={Boolean(fieldErrors.businessNumber)}
                onChange={(event) => {
                  updateField('businessNumber', event.target.value)
                }}
                placeholder="000-00-00000"
              />
              <FieldError message={fieldErrors.businessNumber} />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="operation-verification-contact">
                연락처
              </label>
              <Input
                id="operation-verification-contact"
                value={form.contact}
                aria-invalid={Boolean(fieldErrors.contact)}
                onChange={(event) => {
                  updateField('contact', event.target.value)
                }}
                placeholder="02-000-0000"
              />
              <FieldError message={fieldErrors.contact} />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="운영 정보" />
          <CardBody className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="operation-verification-address">
                구장 운영 주소 <span className="text-xs font-normal text-slateish-500">(선택)</span>
              </label>
              <Input
                id="operation-verification-address"
                value={form.operationAddress}
                maxLength={OPERATION_VERIFICATION_LIMITS.operationAddress}
                aria-invalid={Boolean(fieldErrors.operationAddress)}
                onChange={(event) => {
                  updateField('operationAddress', event.target.value)
                }}
              />
              <FieldError message={fieldErrors.operationAddress} />
            </div>

            <div>
              <label
                className="mb-1.5 block text-sm font-medium text-ink-700"
                htmlFor="operation-verification-started-on"
              >
                운영 시작일 <span className="text-xs font-normal text-slateish-500">(선택)</span>
              </label>
              <Input
                id="operation-verification-started-on"
                type="date"
                value={form.operationStartedOn}
                max={todayKstDate()}
                aria-invalid={Boolean(fieldErrors.operationStartedOn)}
                onChange={(event) => {
                  updateField('operationStartedOn', event.target.value)
                }}
              />
              <FieldError message={fieldErrors.operationStartedOn} />
            </div>

            <OperationVerificationDocumentField
              venueId={venueId}
              document={form.document}
              onChange={(document) => {
                updateField('document', document)
              }}
            />

            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="operation-verification-note">
                추가 안내 사항 <span className="text-xs font-normal text-slateish-500">(선택)</span>
              </label>
              <Textarea
                id="operation-verification-note"
                rows={3}
                value={form.additionalNote}
                maxLength={OPERATION_VERIFICATION_LIMITS.additionalNote}
                aria-invalid={Boolean(fieldErrors.additionalNote)}
                onChange={(event) => {
                  updateField('additionalNote', event.target.value)
                }}
              />
              <FieldError message={fieldErrors.additionalNote} />
            </div>
          </CardBody>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              navigate(-1)
            }}
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={() => {
              void handleSubmit()
            }}
            disabled={submitMutation.isPending || hasUploadingDocument || !canSubmit}
          >
            {submitMutation.isPending ? '제출 중…' : hasUploadingDocument ? '서류 업로드 중…' : '운영 인증 신청'}
          </Button>
        </div>
      </div>
    </>
  )
}

/** 심사 중인 회차가 있을 때(진입 시 prefill 판정, 또는 제출 중 409) 폼 대신 그리는 안내. */
function PendingReviewNotice({
  venueId,
  navigate,
  showAlreadySubmittedNotice,
}: {
  venueId: string
  navigate: NavigateFunction
  showAlreadySubmittedNotice: boolean
}) {
  return (
    <>
      <PageHeader title="운영 인증 신청" />
      <div className="space-y-3">
        {showAlreadySubmittedNotice ? <Alert tone="info">이미 접수되었습니다. 검토 결과를 기다려 주세요.</Alert> : null}
        <Card>
          <CardBody className="space-y-2 py-10 text-center">
            <p className="text-base font-semibold text-ink-700">인증 대기 중</p>
            <p className="text-sm text-slateish-600">{REVIEW_DURATION_NOTICE}</p>
            <p className="mx-auto max-w-md text-xs text-slateish-500">{DEADLINE_WARNING}</p>
            <div className="pt-3">
              <Button
                variant="secondary"
                onClick={() => {
                  navigate(`/venues/${venueId}/dashboard`)
                }}
              >
                대시보드로 돌아가기
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </>
  )
}

/** 제출 성공 직후 — mutation 응답을 그대로 쓴다(재조회하지 않는다, api-spec §171). */
function SubmittedNotice({
  venueId,
  result,
  navigate,
}: {
  venueId: string
  result: OperationVerificationView
  navigate: NavigateFunction
}) {
  return (
    <>
      <PageHeader title="운영 인증 신청" />
      <Card>
        <CardBody className="space-y-2 py-10 text-center">
          <p className="text-base font-semibold text-ink-700">{result.badgeLabel}</p>
          <p className="text-sm text-slateish-600">{REVIEW_DURATION_NOTICE}</p>
          {result.verifiedUntil ? (
            <p className="text-xs text-slateish-500">
              현재 인증은 {formatDate(result.verifiedUntil)}까지 유효합니다 — 검토 중에도 그대로 유지됩니다.
            </p>
          ) : null}
          <p className="mx-auto max-w-md text-xs text-slateish-500">{DEADLINE_WARNING}</p>
          <div className="pt-3">
            <Button
              variant="secondary"
              onClick={() => {
                navigate(`/venues/${venueId}/dashboard`)
              }}
            >
              대시보드로 돌아가기
            </Button>
          </div>
        </CardBody>
      </Card>
    </>
  )
}
